import Alpine, {AlpineComponent, Magics} from 'alpinejs';
import {morph} from '@groupthought/analog-ui/alpine/morph';
import {requestIdleCallback} from '@theme/utils';

const cachedRecommendations = new Map<string, string>();

type ProductComplementaryData = AlpineComponent<{
  fetchProductRecommendations: () => Promise<string | undefined>;
  updateProductRecommendations: () => void;
  abortController: AbortController | null;
  hasLoaded: boolean;
}>;

Alpine.directive('product-complementary', ($el, {expression, modifiers}) => {
  const productId = expression;

  let limit = 1;

  if (modifiers.includes('limit')) {
    const limitIndex = modifiers.indexOf('limit');
    const rawValue = limitIndex >= 0 && limitIndex < modifiers.length - 1 ? modifiers[limitIndex + 1] : undefined;
    if (rawValue) {
      const match = rawValue.match(/([0-9]+)/);
      if (match) limit = Number(match[1]);
    }
  }

  Alpine.bind($el, {
    'x-data'(this: Magics<any>): ProductComplementaryData {
      return {
        abortController: null,
        hasLoaded: false,
        init() {
          if (!this.$el.id) {
            throw new Error('Product complementary element must have an id');
          }
        },
        async fetchProductRecommendations() {
          const sectionId = this.$sectionEl?.id.split('shopify-section-')[1];

          if (!sectionId) return;

          const url = `${window.theme.routes.productRecommendations}?section_id=${sectionId}&product_id=${productId}&limit=${limit}&intent=complementary`;

          const cachedResult = cachedRecommendations.get(url);

          if (cachedResult) {
            this.hasLoaded = true;
            return cachedResult;
          }

          // Abort any in-flight request
          this.abortController?.abort();
          const currentController = new AbortController();
          this.abortController = currentController;

          try {
            // Fetch the updated version of this DOM element from the section rendering API
            const response = await fetch(url, {signal: currentController.signal});
            const result = await response.text();

            // Only set hasLoaded after we have a valid result
            if (!result) return;

            // Parse the result into a DOM tree and find the updated version of this DOM element
            const parsedResult = new DOMParser().parseFromString(result, 'text/html');
            const recommendationHtml = parsedResult.getElementById(this.$el.id)?.outerHTML;

            if (!recommendationHtml) return;

            // Only mark as loaded and cache if we got valid HTML
            this.hasLoaded = true;
            cachedRecommendations.set(url, recommendationHtml);
            return recommendationHtml;
          } catch (error) {
            // Ignore abort errors, but re-throw other errors
            if (error instanceof Error && error.name !== 'AbortError') {
              console.error('Failed to fetch product recommendations:', error);
            }
            return undefined;
          } finally {
            // Only clear the controller if it's still the current one (wasn't replaced by a new request)
            if (this.abortController === currentController) {
              this.abortController = null;
            }
          }
        },
        async updateProductRecommendations() {
          try {
            const result = await this.fetchProductRecommendations();
            if (result) {
              // The shared morph, not Alpine's raw one: it keeps lazy roots' runtime state
              // and closes any open modal <dialog> it removes
              morph(this.$el, result);
            }
          } catch (error) {
            console.error('Failed to update product recommendations:', error);
          }
        },
      };
    },
    // Marks a finished fetch so the wrapper's has-[...]:hidden selector can hide a loaded-empty
    // element without hiding the pre-fetch element, which x-intersect still has to observe
    ':data-loaded'() {
      return (this as any).hasLoaded;
    },
    'x-intersect.once'() {
      if ((this as any).hasLoaded) return;
      requestIdleCallback(() => {
        (this as any).updateProductRecommendations();
      });
    },
  });
});
